import { DateTime } from "luxon";
import { Ticket } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useClientAuth } from "../ClientAuthContext";
import { useClientAbonements } from "../hooks/useClientAbonements";
import { coversText, sortPackages, type ClientPackage } from "../../../lib/packages";
import { getLuxonLocale } from "@/lib/i18n/dateLocales";
import { getCompanyRegistry } from "@/lib/company/companyRegistrySingleton";

/**
 * The client's packages at this salon: what each covers, valid until, used and
 * left, and whether it is active, used up or expired. Hidden when there are none.
 */
export default function MyPackages() {
    const { t, i18n } = useTranslation();
    const { state } = useClientAuth();
    const { data } = useClientAbonements(state.auth?.accessToken ?? null);
    const list = sortPackages(data ?? []);
    if (!list.length) return null;
    const locale = getLuxonLocale(i18n.resolvedLanguage || i18n.language || "en");
    // valid through the end of the salon-local day: show that day, not the viewer's
    const tz = getCompanyRegistry().getTimezone?.() ?? "UTC";

    const status = (p: ClientPackage) =>
        p.status === "active" ? t("widget.client.packages.active", "Active")
            : p.status === "used_up" ? t("widget.client.packages.used_up", "Used up")
                : p.status === "expired" ? t("widget.client.packages.expired", "Expired") : p.status;

    return (
        <div className="px-4 pt-3">
            <div className="mb-1 text-xs font-semibold text-muted-foreground">
                {t("widget.client.packages.title", "My packages")}
            </div>
            {list.map((p) => (
                <div key={p.id} className={"mb-2 rounded-lg border p-3 text-sm " + (p.status === "active" ? "" : "opacity-70")}>
                    <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2 font-medium">
                            <Ticket className="h-4 w-4 text-primary" aria-hidden="true" />
                            <span>{p.title}</span>
                        </div>
                        <span className={"rounded-full px-2 py-0.5 text-[11px] " +
                            (p.status === "active" ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground")}>
                            {status(p)}
                        </span>
                    </div>
                    {p.pool ? (
                        <div className="mt-1">
                            {p.left == null ? t("widget.client.packages.unlimited", "Unlimited visits") : (
                                <>
                                    {t("widget.client.packages.left", "Left: {{left}} of {{total}}", { left: p.left, total: p.total ?? p.left })}
                                    {p.used != null && p.used > 0 && (
                                        <span className="text-muted-foreground"> · {t("widget.client.packages.used", "used {{n}}", { n: p.used })}</span>
                                    )}
                                </>
                            )}
                            <div className="text-xs text-muted-foreground">{coversText(p)}</div>
                        </div>
                    ) : (
                        <ul className="mt-1 space-y-0.5">
                            {p.lines.map((l, i) => (
                                <li key={i} className="flex justify-between gap-2">
                                    <span className="text-muted-foreground">{l.title}</span>
                                    <span>{l.unlimited ? "∞"
                                        : t("widget.client.packages.line_left", "{{left}} of {{total}}", { left: l.left ?? 0, total: l.total ?? l.left ?? 0 })}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-1 text-xs text-muted-foreground">
                        {p.expires_at && t("widget.client.packages.valid_until", "Valid until {{date}}", {
                            date: DateTime.fromISO(p.expires_at).setZone(tz).setLocale(locale).toFormat("d LLLL yyyy"),
                        })}
                        {p.number ? ` · № ${p.number}` : ""}
                    </div>
                </div>
            ))}
        </div>
    );
}
